import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useCallback,
  useImperativeHandle,
} from 'react'
import {
  View,
  StyleSheet,
  TouchableOpacity,
  NativeSyntheticEvent,
} from 'react-native'

import { pdfjs, Document, Page } from 'react-pdf'

import Loading from '../../../components/Loading'
import SvgIcon from '../../../components/SvgIcon'
import appStyles from '../../../styles/app-styles'
import Colors from '../../../styles/Colors'
import { PdfViewHandle, PdfViewProps } from '../../../types/PdfView.types'

// NOTE: Import worker, see: https://github.com/wojtekmaj/react-pdf?tab=readme-ov-file#configure-pdfjs-worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  // @ts-ignore
  import.meta.url,
).toString()

// eslint-disable-next-line react/display-name
export const PdfView = forwardRef<PdfViewHandle, PdfViewProps>(
  ({ pdfUrl, onPageChanged, onConfigurationChange }, ref) => {
    const [pageNumber, setPage] = useState<number>(1)
    const [layout, setLayout] = useState<{ width: number; height: number }>()
    const pageCount = useRef<number>()

    useEffect(() => onPageChanged?.(pageNumber), [onPageChanged, pageNumber])

    useImperativeHandle(ref, () => ({ setPage }))

    const layoutProps = useMemo(() => {
      if (!layout) return { step: 1 as const }

      const pageHeight = layout.height
      const pageWidth = pageHeight * Math.SQRT1_2
      const fitsTwo = pageWidth * 2 + PAGE_GAP <= layout.width
      return {
        fitsTwo,
        width: pageWidth,
        height: pageHeight,
        step: fitsTwo ? 2 : 1,
      } as const
    }, [layout])

    const buttonTopOffsetStyle = useMemo(
      () => ({
        marginTop: (layoutProps.height ?? 0) / 2,
      }),
      [layoutProps.height],
    )

    const onLoadSuccess = useCallback(
      (file: { numPages: number }) => {
        pageCount.current = file.numPages
        onConfigurationChange({
          numberOfPages: file.numPages,
          step: layoutProps.step,
        })
      },
      [onConfigurationChange, layoutProps.step],
    )

    useEffect(() => {
      onConfigurationChange({
        step: layoutProps.step,
        numberOfPages: pageCount.current,
      })
    }, [onConfigurationChange, layoutProps.step])

    const forwardDisabled = pageNumber + layoutProps.step > pageCount.current!
    const backwardDisabled = pageNumber === 1

    const getOnIncrementPagePress = useCallback(
      (direction: 'forward' | 'backward') => {
        return () =>
          setPage(current => {
            const newNumber =
              direction === 'forward'
                ? Math.min(
                    pageCount.current ?? Infinity,
                    current + layoutProps.step,
                  )
                : Math.max(1, current - layoutProps.step)
            return newNumber
          })
      },
      [layoutProps.step, pageCount, setPage],
    )

    const onForwardPress = useCallback(
      () => getOnIncrementPagePress('forward')(),
      [getOnIncrementPagePress],
    )
    const onBackwardPress = useCallback(
      () => getOnIncrementPagePress('backward')(),
      [getOnIncrementPagePress],
    )

    useEffect(() => {
      const onKeydown = (e: NativeSyntheticEvent<{ key: string }>) => {
        switch (e.nativeEvent.key) {
          case 'ArrowLeft':
            onBackwardPress()
            return e.preventDefault()
          case 'ArrowRight':
            onForwardPress()
            return e.preventDefault()
        }
      }
      // @ts-expect-error
      window.addEventListener('keydown', onKeydown)

      // @ts-expect-error
      return () => window.removeEventListener('keydown', onKeydown)
    }, [onForwardPress, onBackwardPress])

    return (
      <View style={styles.container}>
        <View
          style={styles.documentHolder}
          onLayout={e => setLayout(e.nativeEvent.layout)}>
          <Document
            file={pdfUrl}
            loading={<Loading loading />}
            onLoadSuccess={onLoadSuccess}>
            <View style={styles.pagesHolder}>
              {layout && (
                <Page
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  pageNumber={pageNumber}
                  width={layoutProps.width}
                  height={layoutProps.height}
                />
              )}
              {pageNumber < pageCount.current! && layoutProps.fitsTwo && (
                <Page
                  renderTextLayer={false}
                  width={layoutProps.width}
                  pageNumber={pageNumber + 1}
                  height={layoutProps.height}
                  renderAnnotationLayer={false}
                />
              )}
              <TouchableOpacity
                style={[
                  styles.pageButton,
                  buttonTopOffsetStyle,
                  styles.pageButtonBackward,
                  backwardDisabled && styles.pageButtonDisabled,
                ]}
                disabled={backwardDisabled}
                onPress={onBackwardPress}>
                <SvgIcon name="back" color={Colors['layout.black']} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.pageButton,
                  buttonTopOffsetStyle,
                  styles.pageButtonForward,
                  forwardDisabled && styles.pageButtonDisabled,
                ]}
                disabled={forwardDisabled}
                onPress={onForwardPress}>
                <SvgIcon name="back" color={Colors['layout.black']} />
              </TouchableOpacity>
            </View>
          </Document>
        </View>
      </View>
    )
  },
)

const PAGE_GAP = 4

const styles = StyleSheet.create({
  container: {
    ...appStyles.fullSize,
    paddingHorizontal: 53,
    backgroundColor: Colors['layout.light'],
  },
  documentHolder: {
    ...appStyles.fullSize,
    ...appStyles.center,
  },
  pagesHolder: {
    ...appStyles.row,
    gap: PAGE_GAP,
  },
  pageButton: {
    ...appStyles.center,
    top: -25,
    width: 50,
    height: 50,
    borderRadius: 25,
    position: 'absolute',
    ...appStyles.lightShadow,
    backgroundColor: Colors['layout.white'],
  },
  pageButtonDisabled: {
    opacity: 0.3,
  },
  pageButtonBackward: {
    left: -25,
  },
  pageButtonForward: {
    right: -25,
    transform: [{ scaleX: -1 }],
  },
})
